import { useState } from 'preact/hooks';
import { ROTULO_PERFIL } from '../format';
import { Icone } from '../icones';
import type { Rota } from '../permissoes';
import { fazerLogout } from '../session';
import { usuario } from '../state';
import { Modal, confirmar } from '../ui';
import { BotaoInstalar } from './Instalar';
import { SeletorTema } from './Tema';

type ItemMenu = { id: Rota; rotulo: string };

/** Telas que ganham lugar fixo na barra de baixo, na ordem de preferência. As demais vão para a janela "Mais". */
const PRIORIDADE: Rota[] = ['painel', 'acoes', 'impacto', 'pesquisas', 'projetos', 'usuarios'];
const VAGAS_NA_BARRA = 3;

/** Separa o menu do perfil em "fixas na barra" e "dentro de Mais" (sem repetir e sem perder nenhuma). */
export function dividirMenu(menu: ItemMenu[]): { barra: ItemMenu[]; mais: ItemMenu[] } {
  const barra = PRIORIDADE.map((id) => menu.find((m) => m.id === id)).filter((m): m is ItemMenu => !!m).slice(0, VAGAS_NA_BARRA);
  const ids = new Set(barra.map((m) => m.id));
  return { barra, mais: menu.filter((m) => !ids.has(m.id)) };
}

/**
 * Navegação de aplicativo para o celular: barra fixa no rodapé com as telas principais, "Mais" e "Sair".
 * Só aparece em tela estreita (o CSS esconde no computador, onde vale o menu do topo).
 */
export function BarraInferior({ menu, atual }: { menu: ItemMenu[]; atual: Rota }) {
  const [maisAberto, setMaisAberto] = useState(false);
  const { barra, mais } = dividirMenu(menu);
  const atualEstaEmMais = mais.some((m) => m.id === atual);

  async function sair() {
    if (await confirmar('Sair do MAPAS neste aparelho?', { confirmar: 'Sair' })) void fazerLogout();
  }

  return (
    <>
      <nav class="barra-inferior" aria-label="Navegação principal">
        {barra.map((m) => (
          <a key={m.id} href={`#/${m.id}`} class={atual === m.id ? 'barra-item ativo' : 'barra-item'} aria-current={atual === m.id ? 'page' : undefined}>
            <Icone nome={m.id} />
            <span>{m.rotulo}</span>
          </a>
        ))}
        <button type="button" class={atualEstaEmMais ? 'barra-item ativo' : 'barra-item'} aria-haspopup="dialog" onClick={() => setMaisAberto(true)}>
          <Icone nome="mais" />
          <span>Mais</span>
        </button>
        <button type="button" class="barra-item" onClick={() => void sair()}>
          <Icone nome="sair" />
          <span>Sair</span>
        </button>
      </nav>
      {maisAberto && <FolhaMais mais={mais} atual={atual} onFechar={() => setMaisAberto(false)} />}
    </>
  );
}

function FolhaMais({ mais, atual, onFechar }: { mais: ItemMenu[]; atual: Rota; onFechar: () => void }) {
  const u = usuario.value!;
  return (
    <Modal titulo="Mais" onFechar={onFechar} pequeno>
      <div class="folha-conta">
        <strong>{u.nome}</strong>
        <small>{u.email}</small>
        <small>{ROTULO_PERFIL[u.perfil]}</small>
      </div>

      {mais.length > 0 && (
        <nav aria-label="Outras telas">
          <ul class="folha-rotas">
            {mais.map((m) => (
              <li key={m.id}>
                <a href={`#/${m.id}`} class={atual === m.id ? 'folha-rota ativo' : 'folha-rota'} aria-current={atual === m.id ? 'page' : undefined} onClick={onFechar}>
                  <Icone nome={m.id} tamanho={24} />
                  {m.rotulo}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <section aria-label="Aparência">
        <h3>Aparência</h3>
        <SeletorTema />
      </section>

      <BotaoInstalar class="botao folha-instalar" aoClicar={onFechar} rotulo="Instalar o MAPAS como aplicativo" />
    </Modal>
  );
}
